import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { CheckCircle2, Lock } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { getSolutionIcon } from "@/lib/solutionIcons";
import { FrontDeskPanel } from "@/components/org/frontdesk/FrontDeskPanel";
import { SalesWorkspace } from "@/components/org/sales/SalesWorkspace";
import { OfficeWorkspace } from "@/components/org/office/OfficeWorkspace";
import { PeopleWorkspace } from "@/components/org/people/PeopleWorkspace";
import { BillingWorkspace } from "@/components/org/billing/BillingWorkspace";
import { SupportWorkspace } from "@/components/org/support/SupportWorkspace";

export default function OrgSolutionShell() {
  const { slug } = useParams();
  const { API } = useAuth();
  const [solutions, setSolutions] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/org/solutions`, { withCredentials: true })
      .then(({ data }) => setSolutions(data.solutions || []))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-solution-shell-error">{error}</p>;
  if (!solutions) return <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="org-solution-shell-loading">Loading…</div>;

  const solution = solutions.find((s) => s.slug === slug);
  if (!solution) {
    return <EmptyState icon={Lock} title="Not available." message="This Solution isn't enabled for your account, or has not been assigned to your organization." testId="org-solution-shell-denied" />;
  }

  const Icon = getSolutionIcon(solution.icon);
  const activeCapabilities = (solution.capabilities || []).filter((c) => c.status === "Active");

  return (
    <div className="space-y-6" data-testid={`org-solution-shell-${slug}`}>
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 dark:bg-blue-500/10"><Icon size={22} className="text-[#0052FF] dark:text-blue-400" /></span>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">{solution.name}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{solution.description}</p>
        </div>
      </div>
      {slug === "front-desk" && <FrontDeskPanel />}
      {slug === "sales" && <SalesWorkspace />}
      {slug === "office" && <OfficeWorkspace />}
      {slug === "people" && <PeopleWorkspace />}
      {slug === "billing" && <BillingWorkspace accessLevel={solution.access_level} />}
      {slug === "support" && <SupportWorkspace accessLevel={solution.access_level} />}
      {slug !== "front-desk" && slug !== "sales" && slug !== "office" && slug !== "people" && slug !== "billing" && slug !== "support" && (
        <Panel title="Capabilities" subtitle="These are coming soon and will appear here as they go live.">
          {activeCapabilities.length === 0 ? (
            <EmptyState icon={Icon} title="No capabilities configured." message="Nova Opsis Platform Admin has not configured capabilities for this Solution yet." testId="org-solution-shell-empty" />
          ) : (
            <div className="space-y-2" data-testid="org-solution-shell-capabilities">
              {activeCapabilities.map((c) => (
                <div key={c.capability_id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 dark:border-white/10">
                  <CheckCircle2 size={16} className="text-slate-300 dark:text-slate-600" />
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{c.name}</p>
                    {c.description && <p className="text-xs text-slate-500 dark:text-slate-400">{c.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
